<template>
  <div class="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">

      <div class="text-center">
        <img 
          src="/logo-pismozapravdu.png" 
          alt="Pismo za Pravdu Logo" 
          class="mx-auto h-16 w-auto mb-6"
        />
        <h2 class="text-3xl font-extrabold text-gray-900 tracking-tight">
          Admin Panel
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          Pristup samo za volontere
        </p>
      </div>


      <div class="mt-8">
        <div class="bg-white py-8 px-4 shadow-xl rounded-2xl sm:px-10 border border-gray-100">
          <form class="space-y-6" @submit.prevent="handleLogin">
  
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">
                Email adresa
              </label>
              <div class="mt-1">
                <input
                  id="email"
                  v-model="email"
                  name="email"
                  type="email"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm transition-all duration-200"
                  placeholder="vasa@email.com"
                />
              </div>
            </div>


            <div>
              <label for="password" class="block text-sm font-medium text-gray-700">
                Lozinka
              </label>
              <div class="mt-1">
                <input
                  id="password"
                  v-model="password"
                  name="password"
                  type="password"
                  required
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>
            </div>


            <div 
              v-if="error" 
              class="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg"
            >
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-red-700">{{ error }}</p>
                </div>
              </div>
            </div>


            <div>
              <button
                type="submit"
                :disabled="loading"
                class="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg 
                  v-if="loading" 
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading ? 'Prijavljivanje...' : 'Prijavi se' }}
              </button>
            </div>
          </form>


          <div class="mt-6">
            <button
              @click="handleForgotPassword"
              type="button"
              class="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 font-medium focus:outline-none focus:underline transition-colors duration-200"
            >
              Zaboravili ste lozinku?
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 text-center">
      <a 
        href="/" 
        class="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
      >
        ← Nazad na početnu stranu
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '@/lib/supabaseClient'

const router = useRouter()
const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

async function handleLogin() {
  try {
    loading.value = true
    error.value = ''

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: email.value,
      password: password.value
    })

    if (authError) throw authError


    // Ask the backend whether this user is an admin. The data API is not
    // exposed to browsers since the move to self-hosted PostgreSQL, so the
    // previous direct `admin_users` query over PostgREST no longer applies.
    const meResponse = await fetch(`/api/auth/me`, {
      headers: { Authorization: `Bearer ${data.session?.access_token}` }
    })

    if (!meResponse.ok) {
      throw new Error('Neovlašćen pristup')
    }

    router.push('/admin/content')
  } catch (e: any) {
    error.value = e.message === 'Invalid login credentials'
      ? 'Pogrešni podaci za prijavu'
      : e.message || 'Došlo je do greške prilikom prijave'
  } finally {
    loading.value = false
  }
}

async function handleForgotPassword() {
  if (!email.value) {
    error.value = 'Unesite email adresu za reset lozinke'
    return
  }

  try {
    loading.value = true
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.value, {
      redirectTo: `${window.location.origin}/admin/reset-password`
    })
    
    if (resetError) throw resetError
    
    alert('Uputstva za reset lozinke su poslata na vašu email adresu')
  } catch (e: any) {
    error.value = e.message || 'Greška pri slanju instrukcija za reset'
  } finally {
    loading.value = false
  }
}
</script> 